/**
 * Credit-transaction exclusion for customer statements.
 *
 * Statements normally list every transaction with a non-zero outstanding balance
 * (invoices, credit notes, adjustments and unallocated/partially-allocated receipts)
 * and take their headline totals from the customer balance and the server-side
 * ageing bands. When credits are excluded, both the rows and the figures derived
 * from them have to come from the debit transactions alone, so the bands and totals
 * are recomputed here from the same rows the statement prints.
 *
 * Exclusion is by transaction type rather than by amount sign: `OutstandingAmount`
 * inherits the sign of the posted amount, so a reversing document can carry the
 * opposite sign to the rest of its type.
 *
 * Age is measured as at today, matching `v_Transactions_List.DaysOverdue`, so the
 * bands describe the same position as the server bands the statement shows when
 * credits are included.
 */

import type { AgeingBandConfig } from '@/types/ageing-analysis';
import type { Transaction, TransactionType } from '@/types/transaction';
import type { CustomerAgeingUI } from '@/types/ui/customer-detail';

const MS_PER_DAY = 86_400_000;

/** Date-only values are calendar dates, so both sides are normalised before subtracting. */
const DATE_ONLY_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

const CREDIT_TRANSACTION_TYPES: ReadonlySet<TransactionType> = new Set<TransactionType>([
  'credit_note',
  'credit_adjustment',
  'receipt',
]);

/** How transaction age is measured, per the company's metrics settings. */
export type AgeingBasis = 'due-date' | 'transaction-date';

export function isCreditTransaction(transaction: Transaction): boolean {
  return CREDIT_TRANSACTION_TYPES.has(transaction.transactionType);
}

export function filterDebitTransactions(transactions: Transaction[]): Transaction[] {
  return transactions.filter((tx) => !isCreditTransaction(tx));
}

function resolveBaseOutstandingAmount(tx: Transaction): number {
  return tx.outstandingAmount ?? tx.amount;
}

function resolveDisplayOutstandingAmount(tx: Transaction): number {
  return tx.displayOutstandingAmount ?? resolveBaseOutstandingAmount(tx);
}

function calendarDay(value: string | Date): number {
  if (typeof value === 'string') {
    const parts = DATE_ONLY_PREFIX.exec(value);
    if (parts) return Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  }
  const parsed = typeof value === 'string' ? new Date(value) : value;
  return Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/**
 * Days past the due date as at today, clamped at zero like the view's `DaysOverdue`,
 * so a transaction that is not yet due reads as zero rather than negative.
 */
function resolveDaysOverdue(tx: Transaction, today: number): number {
  if (tx.daysOverdue !== undefined && tx.daysOverdue !== null) return Math.max(0, tx.daysOverdue);
  if (tx.dueDate) return Math.max(0, Math.round((today - calendarDay(tx.dueDate)) / MS_PER_DAY));
  return 0;
}

/** Age used for banding: days overdue, or days since the transaction date on that basis. */
function resolveAgeDays(tx: Transaction, today: number, ageingBasis: AgeingBasis, daysOverdue: number): number {
  return ageingBasis === 'transaction-date'
    ? Math.max(0, Math.round((today - calendarDay(tx.transactionDate)) / MS_PER_DAY))
    : daysOverdue;
}

export interface DebitOnlyStatementFigures {
  outstanding: number;
  overdue: number;
  ageing: CustomerAgeingUI;
}

/**
 * Recomputes the statement's outstanding/overdue totals and ageing bands from
 * debit transactions only, in the currency the statement is printed in.
 *
 * Bands are filled using the same rule as `v_Customer_Ageing_Summary`: a
 * transaction lands in the band whose day range contains its age. The overdue
 * total stays on the due date whatever the banding basis, matching the customer
 * balance's own overdue figure (outstanding where `DueDate < today`).
 */
export function buildDebitOnlyStatementFigures(
  debitTransactions: Transaction[],
  ageingBands: AgeingBandConfig[],
  useBase: boolean,
  ageingBasis: AgeingBasis,
): DebitOnlyStatementFigures {
  const resolveOutstandingAmount = useBase ? resolveBaseOutstandingAmount : resolveDisplayOutstandingAmount;
  const today = calendarDay(new Date());
  const totalByBandSequence = new Map<number, number>();
  let outstanding = 0;
  let overdue = 0;

  for (const tx of debitTransactions) {
    const amount = resolveOutstandingAmount(tx);
    const daysOverdue = resolveDaysOverdue(tx, today);
    const ageDays = resolveAgeDays(tx, today, ageingBasis, daysOverdue);
    outstanding += amount;
    if (daysOverdue > 0) overdue += amount;

    const band = ageingBands.find((b) => ageDays >= b.startDays && (b.endDays === null || ageDays <= b.endDays));
    if (band) {
      totalByBandSequence.set(band.sequence, (totalByBandSequence.get(band.sequence) ?? 0) + amount);
    }
  }

  return {
    outstanding,
    overdue,
    ageing: {
      bands: ageingBands.map((band) => ({
        sequence: band.sequence,
        label: band.label,
        bandLabel: band.bandLabel,
        startDays: band.startDays,
        endDays: band.endDays,
        totalOutstanding: totalByBandSequence.get(band.sequence) ?? 0,
        color: band.color,
      })),
      total: outstanding,
      overdue,
      unallocatedReceipts: 0,
    },
  };
}
